import { BASE_PRESET_NAME } from "building-types/doc/docDefaultConstants";
import { docEntityIdFromString, type DocEntityId } from "building-types/doc/docEntityId";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import type { ParameterRef } from "building-types/options/parameter";
import type { ViewportCameraKey } from "building-types/views/CameraKey";
import type {
  ElevationViewport,
  Evaluated,
  EvaluatedViewport,
  EvaluatedWithOptionSetOverrides,
  Viewport,
  ViewportPosition,
  ViewportViewType,
  ViewTemplate,
} from "building-types/views/planSets";
import {
  ViewportScalingMethod,
  ViewportPositioningMethod,
  ViewportHorizontalAlignment,
  ViewportVerticalAlignment,
  DEFAULT_VIEWPORT_SCALE,
} from "building-types/views/planSets";
import type { ImmutableSheetGeometryList } from "building-types/views/sheetGeometryTypes";
import type { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import { BUILT_IN_VIEW_SCALE_1_PER_4_INCH } from "building-types/views/viewScaleConstants";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { degreeToRadian } from "common/utils/math";
import { sheetInchesNew } from "common/utils/nominalNumbers";
import type { Mutable } from "common/utils/types";
import uid from "common/utils/uid";
import sheetGeometryListAABB2 from "doc/plans/sheetGeometryAABB2";
import { aabb2Copy, aabb2Init, aabb2IsValid, aabb2Rotate, aabb2Scale, aabb2UnionMutable } from "geom/aabb2";
import { aabb2Height, aabb2New, aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON } from "geom/core";
import { mat3Dot, type Mat3 } from "geom/mat3";
import { polyLine2AABB2, polyLine2SetCCW, polyLine2TransformCopy } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

const MIN_VIEWPORT_SCALE = 0.001;

export function isAlignedViewportPositioningMethod(positioningMethod: ViewportPositioningMethod | undefined): boolean {
  return positioningMethod === ViewportPositioningMethod.ALIGNED;
}

export function fitViewportPositioningMethod(
  _positioningMethod: ViewportPositioningMethod | undefined,
): ViewportPositioningMethod {
  return ViewportPositioningMethod.ALIGNED;
}

export function effectiveViewportScale(
  viewport: Evaluated<ViewportPosition>,
  planContentAABB?: AABB2,
): number | undefined {
  const viewportAABB = polyLine2AABB2(viewport.outline);
  const viewportWidth = aabb2Width(viewportAABB);
  const viewportHeight = aabb2Height(viewportAABB);
  const { scalingMethod = ViewportScalingMethod.FIT } = viewport;

  const userDefinedScale = viewport.scale ?? DEFAULT_VIEWPORT_SCALE;

  function planContentDeterminedScale() {
    return planContentAABB && viewportContentAABBIsValid(planContentAABB)
      ? Math.min(viewportWidth / aabb2Width(planContentAABB), viewportHeight / aabb2Height(planContentAABB))
      : undefined;
  }

  const scale: number | undefined =
    scalingMethod === ViewportScalingMethod.FIT || userDefinedScale < MIN_VIEWPORT_SCALE
      ? planContentDeterminedScale()
      : userDefinedScale;

  return scale;
}

/**
 *
 * @param viewport The viewport to calculate the scale for
 * @param planContentAABB The bounding box of the content in world scale
 * @returns A scale factor and position to apply to the plan content
 */
export function calculateViewportContentPositionAndScale(
  viewport: Evaluated<ViewportPosition>,
  planContentAABB: AABB2,
  alignmentContentAABB: AABB2 = planContentAABB,
): { scale: number; x: SheetInches; y: SheetInches } {
  const viewportAABB = polyLine2AABB2(viewport.outline);
  const viewportWidth = aabb2Width(viewportAABB);
  const viewportHeight = aabb2Height(viewportAABB);
  const { scalingMethod = ViewportScalingMethod.FIT, positioningMethod = ViewportPositioningMethod.ALIGNED } = viewport;

  const scalePerimeterAABB = scalingMethod === ViewportScalingMethod.FIT ? alignmentContentAABB : planContentAABB;

  const scale = effectiveViewportScale(viewport, scalePerimeterAABB) ?? BUILT_IN_VIEW_SCALE_1_PER_4_INCH;

  let x = 0;
  let y = 0;

  if (isAlignedViewportPositioningMethod(positioningMethod) || scalingMethod === ViewportScalingMethod.FIT) {
    let alignmentPerimeterAABB = alignmentContentAABB;

    if (!viewportContentAABBIsValid(alignmentPerimeterAABB)) {
      // We might be defining a viewport that has empty content for the current building.
      // Use a default content AABB around the origin to still align the viewport position to some reasonable value.
      alignmentPerimeterAABB = aabb2New(0, 0, 1, 1);
    }
    const scaledPlanContentAABB = aabb2Scale(aabb2Copy(alignmentPerimeterAABB), scale);
    const scaledPlanContentWidth = aabb2Width(scaledPlanContentAABB);
    const scaledPlanContentHeight = aabb2Height(scaledPlanContentAABB);

    const {
      horizontalAlignment = ViewportHorizontalAlignment.CENTER,
      verticalAlignment = ViewportVerticalAlignment.CENTER,
    } = viewport;

    switch (horizontalAlignment) {
      case "LEFT": {
        x = viewportAABB.minX - scaledPlanContentAABB.minX;
        break;
      }
      case "RIGHT": {
        x = viewportAABB.minX + viewportWidth - scaledPlanContentAABB.minX - scaledPlanContentWidth;
        break;
      }
      case "CENTER":
      default: {
        x = viewportAABB.minX + viewportWidth / 2 - scaledPlanContentAABB.minX - scaledPlanContentWidth / 2;
        break;
      }
    }

    switch (verticalAlignment) {
      case "TOP": {
        y = viewportAABB.minY + viewportHeight - scaledPlanContentAABB.minY - scaledPlanContentHeight;
        break;
      }
      case "BOTTOM": {
        y = viewportAABB.minY - scaledPlanContentAABB.minY;
        break;
      }
      case "CENTER":
      default: {
        y = viewportAABB.minY + viewportHeight / 2 - scaledPlanContentAABB.minY - scaledPlanContentHeight / 2;
        break;
      }
    }
  } else {
    x = viewport.worldOriginInPageSpaceX;
    y = viewport.worldOriginInPageSpaceY;
  }

  return { scale, x: sheetInchesNew(x), y: sheetInchesNew(y) };
}

export function convertViewportPropsForExplicitPosition(
  viewport: Viewport,
  evaluatedViewport: EvaluatedViewport,
  viewportContentAABB: AABB2,
  alignmentContentAABB: AABB2 = viewportContentAABB,
): Pick<Viewport, "worldOriginInPageSpaceX" | "worldOriginInPageSpaceY"> & Partial<Viewport> {
  if (viewport.positioningMethod === ViewportPositioningMethod.EXPLICIT) {
    return viewport;
  }
  const { x, y, scale } = calculateViewportContentPositionAndScale(
    evaluatedViewport,
    viewportContentAABB,
    alignmentContentAABB,
  );
  const props: Mutable<Pick<Viewport, "worldOriginInPageSpaceX" | "worldOriginInPageSpaceY"> & Partial<Viewport>> = {
    positioningMethod: ViewportPositioningMethod.EXPLICIT,
    worldOriginInPageSpaceX: x,
    worldOriginInPageSpaceY: y,
  };

  if (viewport.scalingMethod !== ViewportScalingMethod.EXPLICIT) {
    props.scalingMethod = ViewportScalingMethod.EXPLICIT;
    props.scale = scale;
  }

  return props;
}

/**
 * The viewport origin on the sheet is the bottom left corner of the outline's bounding box.
 */
function viewportSheetSpaceOrigin(viewport: Pick<ViewportPosition, "outline">): { x: SheetInches; y: SheetInches } {
  const aabb = polyLine2AABB2(viewport.outline);
  return { x: sheetInchesNew(aabb.minX), y: sheetInchesNew(aabb.minY) };
}

/**
 * Creates a ViewportPosition and initializes worldOriginInPageSpaceX and worldOriginInPageSpaceY to the
 * origin of the outline.
 */
export function makeViewportPositionFromOutline(
  outline: PolyLine2,
): Pick<ViewportPosition, "outline" | "worldOriginInPageSpaceX" | "worldOriginInPageSpaceY"> {
  const { x, y } = viewportSheetSpaceOrigin({ outline });
  return {
    outline,
    worldOriginInPageSpaceX: x,
    worldOriginInPageSpaceY: y,
  };
}

export function transformViewportPosition(
  viewport: ViewportPosition,
  mat: Mat3,
  updateSheetSpaceOrigin: boolean = true,
): ViewportPosition {
  const outline = polyLine2SetCCW(polyLine2TransformCopy(viewport.outline, mat));
  if (!updateSheetSpaceOrigin) {
    return {
      outline,
      worldOriginInPageSpaceX: viewport.worldOriginInPageSpaceX,
      worldOriginInPageSpaceY: viewport.worldOriginInPageSpaceY,
    };
  }
  const [x, y] = mat3Dot(mat, viewport.worldOriginInPageSpaceX, viewport.worldOriginInPageSpaceY);
  return { outline, worldOriginInPageSpaceX: sheetInchesNew(x), worldOriginInPageSpaceY: sheetInchesNew(y) };
}

interface ViewportNewOptions {
  readonly id?: string;
  readonly name?: string | undefined;
  readonly outline: PolyLine2;
  readonly viewType: ViewportViewType;
  readonly viewTemplateId: Viewport["viewTemplateId"];
  readonly contentSourceId: string;
  readonly contentSourceComponentInstanceChain?: string[];
}

export function viewportNew({
  id = uid(),
  outline,
  viewType,
  viewTemplateId,
  contentSourceId,
  contentSourceComponentInstanceChain,
  name = "Viewport",
}: ViewportNewOptions): Viewport {
  const position = makeViewportPositionFromOutline(outline);

  const baseProps = {
    ...docEntityBaseNew("VIEWPORT"),
    ...position,
    id,
    viewType,
    viewTemplateId,
    presetId: docEntityIdFromString("PRESET", BASE_PRESET_NAME),
    optionSetOverrides: {},
    name,
    scalingMethod: ViewportScalingMethod.FIT,
    positioningMethod: ViewportPositioningMethod.ALIGNED,
    horizontalAlignment: ViewportHorizontalAlignment.CENTER,
    verticalAlignment: ViewportVerticalAlignment.CENTER,
    viewTemplateOverrideIds: [],
  };

  switch (viewType) {
    case "PLAN_VIEW":
      return {
        ...baseProps,
        viewType,
        contentSourceId: contentSourceId as DocEntityId<"LEVEL"> | DocEntityId<"COMPOSITE_LEVEL">,
        ...(contentSourceComponentInstanceChain?.length ? { contentSourceComponentInstanceChain } : {}),
      };
    case "ELEVATION_VIEW":
      return {
        ...baseProps,
        viewType,
        contentSourceId: docEntityIdFromString("ELEVATION_VIEW", contentSourceId),
      };
    case "SECTION_VIEW":
      return {
        ...baseProps,
        viewType,
        contentSourceId: docEntityIdFromString("SECTION_VIEW", contentSourceId),
      };
    case "VIEW_3D":
      return {
        ...baseProps,
        viewType,
        contentSourceId: contentSourceId as ViewportCameraKey,
      };
    case "DRAWING":
      return {
        ...baseProps,
        viewType,
        parameterValues: {},
        contentSourceId: docEntityIdFromString("CONTENT_TEMPLATE", contentSourceId),
      };
    default:
      assertUnreachable(viewType);
  }
}

export function viewportNameForViewTemplateNew(viewTemplate: ViewTemplate | null): string | undefined {
  if (!viewTemplate) {
    return undefined;
  }

  return `${viewTemplate.name} Viewport`;
}

export function viewportContentRotationRad(viewport: EvaluatedViewport): number {
  return (viewport.viewType === "PLAN_VIEW" || viewport.viewType === "DRAWING") &&
    typeof viewport.contentRotationDegrees === "number"
    ? degreeToRadian(-viewport.contentRotationDegrees) // Positive rotation is intended to be counter-clockwise.
    : 0;
}

interface UnionViewportContentAABBsOptions {
  readonly target?: AABB2;
  readonly sourceGeomLists?: readonly (ImmutableSheetGeometryList | undefined)[];
  readonly sourceAABBs?: (AABB2 | undefined)[];
  readonly rotationRads?: number;
  readonly geometryTypeFilter?: { [K in SheetGeometryType]?: boolean };
}

export function unionViewportContentAABBs({
  target = aabb2Init(),
  sourceGeomLists = [],
  sourceAABBs = [],
  rotationRads = 0,
  geometryTypeFilter,
}: UnionViewportContentAABBsOptions): AABB2 {
  for (const list of sourceGeomLists) {
    if (list) {
      aabb2UnionMutable(target, sheetGeometryListAABB2(list, -rotationRads, geometryTypeFilter));
    }
  }

  for (const aabb of sourceAABBs) {
    if (aabb) {
      aabb2UnionMutable(target, aabb2Rotate(aabb, -rotationRads));
    }
  }

  return target;
}

export function viewportContentAABBIsValid(aabb: AABB2): boolean {
  return aabb2IsValid(aabb) && aabb2Width(aabb) > DIST_EPSILON && aabb2Height(aabb) > DIST_EPSILON;
}

export function viewportContentSourceNameFromViewType(viewType: ViewportViewType) {
  switch (viewType) {
    case "PLAN_VIEW":
      return "Level";
    case "ELEVATION_VIEW":
      return "Elevation View";
    case "SECTION_VIEW":
      return "Section View";
    case "VIEW_3D":
      return "View 3D";
    case "DRAWING":
      return "Drawing Template";
    default:
      return "";
  }
}

export const viewportTypeToPrettyName: {
  [key in ViewportViewType]: string;
} = {
  PLAN_VIEW: "Plan Viewport",
  ELEVATION_VIEW: "Elevation Viewport",
  VIEW_3D: "Perspective Viewport",
  SECTION_VIEW: "Section Viewport",
  DRAWING: "Drawing Viewport",
};

export const viewTypeToContentSourceType: {
  [key in ViewportViewType]: DocEntityType;
} = {
  PLAN_VIEW: "LEVEL",
  ELEVATION_VIEW: "ELEVATION_VIEW",
  VIEW_3D: "VIEW_3D",
  SECTION_VIEW: "SECTION_VIEW",
  DRAWING: "CONTENT_TEMPLATE",
} as const;

export function isElevationViewport(
  viewport: EvaluatedViewport,
): viewport is EvaluatedWithOptionSetOverrides<ElevationViewport> {
  return viewport.viewType === "ELEVATION_VIEW";
}

export function getViewportTitle(viewport: Viewport): ParameterRef | string {
  return viewport.title ?? viewport.name;
}
